import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { auth } from '@/lib/api';
import { Loader2, ExternalLink, TrendingUp, TrendingDown, Minus, Globe2, LandPlot, Users, Briefcase, Info, MapPin, Newspaper, ArrowRight, Search, Upload, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { rounds as roundsApi, quotas as quotasApi, states as statesApi, updates as updatesApi, occupations as occupationsApi } from '@/lib/api';

const SUBCLASS_OPTIONS = [
  { v: 'all', l: 'All subclasses' },
  { v: '189', l: '189 Skilled Independent' },
  { v: '190', l: '190 State Nominated' },
  { v: '491', l: '491 Regional (Family)' },
  { v: '858', l: '858 National Innovation' },
  { v: '188', l: '188 Business Innovation' },
];

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus };
const TREND_TONE = { up: 'text-red-600', down: 'text-emerald-600', flat: 'text-slate-500' };

export default function MigrationIntelPage() {
  return (
    <div className="p-6" data-testid="migration-intel-page">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#2ECC9A]/15">
          <Globe2 className="h-4 w-4 text-[#2ECC9A]" />
        </div>
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Migration intel</h1>
          <div className="text-sm text-slate-500">One source of truth — federal quotas, invitation rounds, state programs, and every relevant update.</div>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="mt-6">
        <TabsList data-testid="intel-tabs" className="bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="intel-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="rounds" data-testid="intel-tab-rounds">Invitation rounds</TabsTrigger>
          <TabsTrigger value="quotas" data-testid="intel-tab-quotas">Federal quotas</TabsTrigger>
          <TabsTrigger value="states" data-testid="intel-tab-states">State & Territory</TabsTrigger>
          <TabsTrigger value="occupations" data-testid="intel-tab-occupations">Occupation lookup</TabsTrigger>
          <TabsTrigger value="updates" data-testid="intel-tab-updates">Updates</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4 space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2"><InvitationRoundsCard /></div>
            <FederalQuotaCard />
          </div>
          <ProgramYearBreakdownCard />
        </TabsContent>

        <TabsContent value="rounds" className="mt-4"><InvitationRoundsCard /></TabsContent>
        <TabsContent value="quotas" className="mt-4 space-y-6">
          <FederalQuotaCard />
          <ProgramYearBreakdownCard />
        </TabsContent>
        <TabsContent value="states" className="mt-4"><StatesCard /></TabsContent>
        <TabsContent value="occupations" className="mt-4"><OccupationLookupCard /></TabsContent>
        <TabsContent value="updates" className="mt-4"><UpdatesHubCard /></TabsContent>
      </Tabs>
    </div>
  );
}


// ─────────────────── SkillSelect Invitation Rounds ───────────────────
function InvitationRoundsCard() {
  const [sc, setSc] = React.useState('all');
  const [state, setState] = React.useState({ items: [], summary: [], source_url: '' });
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    roundsApi.list({ ...(sc !== 'all' ? { subclass: sc } : {}), limit: 24 })
      .then(setState)
      .finally(() => setLoading(false));
  }, [sc]);

  React.useEffect(load, [load]);

  return (
    <Card className="border-slate-200" data-testid="rounds-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <LandPlot className="h-4 w-4 text-[#2ECC9A]" /> SkillSelect invitation rounds
            </div>
            <div className="text-xs text-slate-500">Points cut-offs and invitations issued each month. Auto-refreshed monthly.</div>
          </div>
          <Select value={sc} onValueChange={setSc}>
            <SelectTrigger data-testid="rounds-subclass" className="h-9 w-56 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{SUBCLASS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {state.summary.length > 0 && (
          <div className="mt-4 grid gap-2 md:grid-cols-2" data-testid="rounds-summary">
            {state.summary.map((s) => {
              const Icon = TREND_ICON[s.trend] || Minus;
              return (
                <div key={s.subclass} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2">
                  <div>
                    <div className="text-xs font-semibold text-[#1A3A5C]">Subclass {s.subclass}</div>
                    <div className="text-[10px] text-slate-500">Latest {s.latest_round} · cutoff {s.latest_cutoff || '—'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-lg font-bold text-[#1A3A5C]">{s.latest_min_points}</div>
                      <div className="text-[9px] uppercase tracking-wider text-slate-400">min pts</div>
                    </div>
                    <Icon className={`h-4 w-4 ${TREND_TONE[s.trend] || 'text-slate-500'}`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : state.items.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
            No invitation-round records yet.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3 font-medium">Round</th>
                  <th className="py-2 pr-3 font-medium">Subclass</th>
                  <th className="py-2 pr-3 font-medium">Min pts</th>
                  <th className="py-2 pr-3 font-medium">Cut-off DOE</th>
                  <th className="py-2 pr-3 font-medium">Invitations</th>
                  <th className="py-2 pr-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody data-testid="rounds-tbody">
                {state.items.map((r, idx) => (
                  <tr key={`${r.round_date}-${r.subclass}-${idx}`} data-testid={`round-row-${idx}`} className="border-t border-slate-100">
                    <td className="py-2 pr-3 font-medium text-[#1A3A5C]">{r.round_date}</td>
                    <td className="py-2 pr-3"><Badge variant="outline" className="border-slate-200 text-[10px]">{r.subclass}</Badge></td>
                    <td className="py-2 pr-3 font-semibold text-[#1A3A5C]">{r.min_points}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.cutoff_date || '—'}</td>
                    <td className="py-2 pr-3 text-slate-600">{(r.invitations_issued || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-[10px] text-slate-500">{r.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {state.source_url && (
          <a
            href={state.source_url} target="_blank" rel="noopener noreferrer"
            data-testid="rounds-source-link"
            className="mt-4 inline-flex items-center gap-1 text-[11px] text-[#1A3A5C] hover:text-[#2ECC9A]"
          >
            Verify at source (immi.homeaffairs.gov.au) <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────── Federal quota — current program year ───────────────────
function FederalQuotaCard() {
  const [state, setState] = React.useState({ items: [], program_year: null, total: 0, source_url: '' });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    quotasApi.current().then(setState).finally(() => setLoading(false));
  }, []);

  return (
    <Card className="border-slate-200" data-testid="quota-current-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <Briefcase className="h-4 w-4 text-[#2ECC9A]" /> Migration program {state.program_year || '—'}
        </div>
        <div className="text-xs text-slate-500">Federal planning levels announced by DHA.</div>

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="mt-4 rounded-md bg-gradient-to-br from-[#2ECC9A]/10 to-transparent p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total places</div>
              <div className="mt-0.5 text-2xl font-bold text-[#1A3A5C]" data-testid="quota-total">{state.total.toLocaleString()}</div>
            </div>

            <div className="mt-3 space-y-2 max-h-80 overflow-auto" data-testid="quota-items">
              {state.items.map((q, i) => (
                <div key={`${q.program_year}-${q.stream}-${i}`} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-[#1A3A5C]">{q.stream}</div>
                    <div className="text-[10px] text-slate-500">{q.category} · sc {q.subclass}</div>
                  </div>
                  <div className="ml-2 text-right">
                    <div className="text-sm font-semibold text-[#1A3A5C]">{(q.planning_level || 0).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            {state.source_url && (
              <a
                href={state.source_url} target="_blank" rel="noopener noreferrer"
                data-testid="quota-source-link"
                className="mt-4 inline-flex items-center gap-1 text-[11px] text-[#1A3A5C] hover:text-[#2ECC9A]"
              >
                Verify at source (DHA) <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────── Multi-year totals ───────────────────
function ProgramYearBreakdownCard() {
  const [state, setState] = React.useState({ items: [], grand_totals: [] });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    quotasApi.list().then(setState).finally(() => setLoading(false));
  }, []);

  return (
    <Card className="border-slate-200" data-testid="quota-history-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <Users className="h-4 w-4 text-[#2ECC9A]" /> Program planning by year
        </div>
        <div className="text-xs text-slate-500">Skill vs Family split across program years.</div>

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="quota-grand-totals">
            {state.grand_totals.map((y) => {
              const skillPct = y.total ? Math.round((y.skill / y.total) * 100) : 0;
              return (
                <div key={y.program_year} className="rounded-md border border-slate-200 p-3" data-testid={`quota-year-${y.program_year}`}>
                  <div className="text-sm font-semibold text-[#1A3A5C]">{y.program_year}</div>
                  <div className="mt-1 text-xl font-bold text-[#1A3A5C]">{y.total.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">total places</div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full bg-[#2ECC9A]" style={{ width: `${skillPct}%` }} />
                  </div>
                  <div className="mt-1 flex justify-between text-[10px] text-slate-500">
                    <span>Skill · {y.skill.toLocaleString()}</span>
                    <span>Family · {y.family.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2 text-[11px] text-slate-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          Numbers reflect DHA-published planning levels for the given program year (1 Jul – 30 Jun). State/territory nominations count against subclass 190/491 totals and are allocated across 8 jurisdictions — Phase D will surface per-state splits.
        </div>
      </CardContent>
    </Card>
  );
}


// ─────────────────── State & Territory ───────────────────
function StatesCard() {
  const [state, setState] = React.useState({ items: [], total_190: 0, total_491: 0 });
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    statesApi.list().then((r) => {
      setState(r);
      setSelected(r.items?.[0] || null);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <Card className="border-slate-200" data-testid="states-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <MapPin className="h-4 w-4 text-[#2ECC9A]" /> State & Territory nomination programs
            </div>
            <div className="text-xs text-slate-500">All 8 jurisdictions · combined {state.total_190?.toLocaleString()} × 190 + {state.total_491?.toLocaleString()} × 491 places.</div>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-4 grid gap-4 lg:grid-cols-[240px_1fr]">
            <div className="space-y-1" data-testid="states-list">
              {state.items.map((s) => (
                <button
                  key={s.state}
                  data-testid={`state-tab-${s.state}`}
                  onClick={() => setSelected(s)}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                    selected?.state === s.state
                      ? 'border-[#2ECC9A] bg-[#2ECC9A]/5 text-[#1A3A5C]'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <div>
                    <div className="text-sm font-semibold text-[#1A3A5C]">{s.state}</div>
                    <div className="text-[10px] text-slate-500">190: {s.allocation_190?.toLocaleString()} · 491: {s.allocation_491?.toLocaleString()}</div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                </button>
              ))}
            </div>

            {selected && (
              <div className="rounded-md border border-slate-200 p-4" data-testid={`state-detail-${selected.state}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-[#1A3A5C]">{selected.state} · {selected.program_year}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{selected.invitation_cadence}</div>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[#1A3A5C]">190 · {selected.allocation_190?.toLocaleString()}</Badge>
                    <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">491 · {selected.allocation_491?.toLocaleString()}</Badge>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Streams</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(selected.streams || []).map((st, i) => (
                      <Badge key={i} variant="outline" className="border-slate-200 text-[10px] text-slate-600">{st}</Badge>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Eligibility summary</div>
                  <div className="mt-1 text-xs text-slate-700">{selected.eligibility_summary}</div>
                </div>

                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Key requirements</div>
                  <ul className="mt-1 list-disc pl-4 text-xs text-slate-700">
                    {(selected.key_requirements || []).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>

                <div className="mt-4 flex flex-wrap gap-3 border-t border-slate-100 pt-3 text-[11px]">
                  {selected.source_url && (
                    <a href={selected.source_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`state-source-${selected.state}`}
                       className="inline-flex items-center gap-1 text-[#1A3A5C] hover:text-[#2ECC9A]">
                      Verify at source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {selected.occupation_list_url && (
                    <a href={selected.occupation_list_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-[#1A3A5C] hover:text-[#2ECC9A]">
                      Occupation list <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────── Unified Updates Hub ───────────────────
const SOURCE_LABEL = {
  policy: 'Policy',
  dha: 'DHA',
  omara: 'OMARA',
  acs: 'ACS',
  legend: 'LegEnd',
  news: 'News',
  skillselect: 'SkillSelect',
  federal_quota: 'Federal quota',
  state_nomination: 'State/Territory',
};

const SOURCE_TONE = {
  policy: 'border-amber-200 bg-amber-50 text-amber-800',
  dha: 'border-amber-200 bg-amber-50 text-amber-800',
  omara: 'border-purple-200 bg-purple-50 text-purple-800',
  acs: 'border-purple-200 bg-purple-50 text-purple-800',
  legend: 'border-purple-200 bg-purple-50 text-purple-800',
  news: 'border-slate-200 bg-slate-50 text-slate-700',
  skillselect: 'border-sky-200 bg-sky-50 text-sky-700',
  federal_quota: 'border-[#2ECC9A]/40 bg-[#2ECC9A]/10 text-[#1A3A5C]',
  state_nomination: 'border-emerald-200 bg-emerald-50 text-emerald-800',
};

function UpdatesHubCard() {
  const [state, setState] = React.useState({ items: [], facets: {} });
  const [loading, setLoading] = React.useState(true);
  const [source, setSource] = React.useState('all');

  const load = React.useCallback(() => {
    setLoading(true);
    updatesApi.list({ limit: 60, ...(source !== 'all' ? { source } : {}) })
      .then(setState)
      .finally(() => setLoading(false));
  }, [source]);

  React.useEffect(load, [load]);

  return (
    <Card className="border-slate-200" data-testid="updates-hub-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <Newspaper className="h-4 w-4 text-[#2ECC9A]" /> Migration updates hub
            </div>
            <div className="text-xs text-slate-500">Every DHA / OMARA / state / SkillSelect item in one chronological feed.</div>
          </div>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="updates-source-filter" className="h-9 w-52 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="skillselect">SkillSelect rounds</SelectItem>
              <SelectItem value="federal_quota">Federal quotas</SelectItem>
              <SelectItem value="state_nomination">State/Territory</SelectItem>
              <SelectItem value="policy">Policy & news</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : state.items.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
            No updates for this filter.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100" data-testid="updates-list">
            {state.items.map((u) => (
              <li key={u.id} data-testid={`update-item-${u.id}`} className="flex items-start gap-3 py-3">
                <Badge variant="outline" className={`shrink-0 text-[10px] ${SOURCE_TONE[u.source] || SOURCE_TONE.news}`}>
                  {SOURCE_LABEL[u.source] || u.source}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#1A3A5C]">{u.title}</div>
                  <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">{u.summary}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                    <span>{new Date(u.published).toLocaleDateString('en-AU')}</span>
                    {u.subclasses?.map((sc) => (<span key={sc} className="rounded bg-slate-100 px-1.5 py-0.5">sc {sc}</span>))}
                    {u.states?.map((s) => (<span key={s} className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">{s}</span>))}
                    {u.url && (
                      <a href={u.url} target="_blank" rel="noopener noreferrer"
                         className="ml-auto inline-flex items-center gap-0.5 text-[#1A3A5C] hover:text-[#2ECC9A]">
                        Source <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}



// ─────────────────── Occupation Cross-Reference ───────────────────
function OccupationLookupCard() {
  const [anzsco, setAnzsco] = React.useState('261313');
  const [curated, setCurated] = React.useState([]);
  const [result, setResult] = React.useState(null);
  const [assessing, setAssessing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    occupationsApi.anzscoList().then((r) => setCurated(r.items || []));
  }, []);

  const lookup = React.useCallback(async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const [cr, sa] = await Promise.all([
        occupationsApi.crossReference(code),
        occupationsApi.skillsAssessment({ anzsco: code }),
      ]);
      setResult(cr);
      setAssessing(sa);
    } catch {
      setResult(null);
      setAssessing(null);
    } finally { setBusy(false); }
  }, []);

  React.useEffect(() => { lookup(anzsco); }, [lookup, anzsco]);

  return (
    <Card className="border-slate-200" data-testid="occupation-lookup-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <Search className="h-4 w-4 text-[#2ECC9A]" /> Occupation cross-reference
            </div>
            <div className="text-xs text-slate-500">Enter an ANZSCO code to see which states and subclasses accept it — with deep-links to each state's live occupation list.</div>
          </div>
        </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input
              data-testid="anzsco-input"
              value={anzsco}
              onChange={(e) => setAnzsco(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="e.g. 261313"
              className="h-9 w-40 font-mono"
            />
            <Button data-testid="anzsco-lookup-btn" onClick={() => lookup(anzsco)} disabled={busy || !anzsco} variant="outline" className="h-9 gap-1 border-slate-200 text-xs">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Lookup
            </Button>
            <div className="ml-2 flex flex-wrap gap-1">
              {curated.slice(0, 8).map((o) => (
                <button
                  key={o.anzsco}
                  data-testid={`anzsco-suggest-${o.anzsco}`}
                  onClick={() => setAnzsco(o.anzsco)}
                  className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] text-slate-600 hover:border-[#2ECC9A] hover:text-[#2ECC9A]"
                  title={o.title}
                >
                  {o.anzsco}
                </button>
              ))}
            </div>
            <div className="ml-auto text-[10px] text-slate-500">{curated.length} codes in register</div>
          </div>

          <AnzscoImportRow onImported={() => occupationsApi.anzscoList().then((r) => setCurated(r.items || []))} />

        {result && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="anzsco-result">
            <div className="rounded-md border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Occupation</div>
              <div className="mt-1 text-lg font-semibold text-[#1A3A5C]">{result.title || 'Unknown ANZSCO code'}</div>
              {result.note && <div className="mt-2 text-xs text-amber-700">{result.note}</div>}
              <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500">Eligible subclasses</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {result.subclasses.map((sc) => (
                  <Badge key={sc} variant="outline" className="border-sky-200 bg-sky-50 text-[10px] text-sky-700">{sc}</Badge>
                ))}
                {result.subclasses.length === 0 && <span className="text-xs text-slate-500">—</span>}
              </div>
              {assessing?.body && (
                <div className="mt-4 rounded-md border border-purple-200 bg-purple-50/40 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-purple-800">Assessing body</div>
                  <div className="mt-0.5 text-sm font-semibold text-[#1A3A5C]">{assessing.body} · <span className="text-slate-600">{assessing.name}</span></div>
                  <div className="mt-1 text-[11px] text-slate-600">~{assessing.expected_weeks} weeks · {assessing.notes}</div>
                  {assessing.url && (
                    <a href={assessing.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-purple-800 hover:text-purple-900">
                      Assessing body site <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-md border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Accepting jurisdictions</div>
              {result.states.length === 0 ? (
                <div className="mt-2 text-xs text-slate-500">No states in our register accept this code — verify at DHA source lists.</div>
              ) : (
                <ul className="mt-2 divide-y divide-slate-100">
                  {result.states.map((s) => (
                    <li key={s.state} data-testid={`anzsco-state-${s.state}`} className="flex items-center justify-between py-2">
                      <div>
                        <div className="text-sm font-semibold text-[#1A3A5C]">{s.state}</div>
                        <div className="text-[10px] text-slate-500">190: {s.allocation_190?.toLocaleString()} · 491: {s.allocation_491?.toLocaleString()}</div>
                      </div>
                      {s.occupation_list_url && (
                        <a href={s.occupation_list_url} target="_blank" rel="noopener noreferrer"
                           data-testid={`anzsco-state-list-${s.state}`}
                           className="inline-flex items-center gap-1 text-[11px] text-[#1A3A5C] hover:text-[#2ECC9A]">
                          Occupation list <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ─────────────────── Bulk ANZSCO CSV import (super admin only) ───────────────────
function AnzscoImportRow({ onImported }) {
  const user = auth.currentUser();
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');
  const fileRef = React.useRef(null);
  if (user?.role !== 'super_admin') return null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setResult(null); setErr('');
    try {
      const r = await occupationsApi.importCsv(f);
      setResult(r);
      onImported?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50/50 p-3" data-testid="anzsco-import-row">
      <div className="flex-1">
        <div className="text-xs font-semibold text-[#1A3A5C]">Bulk import DHA Skilled Occupation List (CSV)</div>
        <div className="text-[10px] text-slate-500">Columns: <span className="font-mono">anzsco</span>, <span className="font-mono">title</span>, <span className="font-mono">states</span> (pipe-separated), <span className="font-mono">subclasses</span>. Existing codes are upserted.</div>
      </div>
      <Button
        data-testid="anzsco-import-btn"
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="h-8 gap-1 border-slate-200 text-xs"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Choose CSV
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv"
        onChange={upload}
        className="hidden"
        data-testid="anzsco-import-file"
      />
      {result && (
        <Badge variant="outline" className="border-[#2ECC9A]/40 bg-white text-[10px] text-[#1A3A5C]" data-testid="anzsco-import-result">
          <CheckCircle2 className="mr-1 h-3 w-3 text-[#2ECC9A]" />
          Imported {result.inserted} · updated {result.updated}{result.skipped ? ` · skipped ${result.skipped}` : ''}
        </Badge>
      )}
      {err && <div className="text-[10px] text-red-600">{err}</div>}
    </div>
  );
}

